import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HorizontalScrollList from './HorizontalScrollList';
import Button from './common/ui/Button';
import useViewerIdentity from '../hooks/useViewerIdentity';
import { getMyReadingHistory } from '../services/usersApi';
import { getNovelsBatch } from '../services/novelsApi';
import { getAllLocalReadingProgress } from '../utils/readingProgressStorage';
import { getNovelImageUrl } from '../utils/imageUtils';
import './ContinueReading.css';

const ContinueReading = () => {
  const navigate = useNavigate();
  const { userId, sessionId } = useViewerIdentity();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    let data = [];

    if (userId) {
      const response = await getMyReadingHistory();
      data = response || [];
    } else if (sessionId) {
      const localProgress = getAllLocalReadingProgress(sessionId);
      if (localProgress.length > 0) {
        const recent = localProgress.slice(0, 10);
        const novelIds = recent.map((item) => item.novelId);
        const novels = await getNovelsBatch(novelIds);
        const novelsMap = new Map(novels.map((n) => [n.id, n]));

        data = recent
          .map((item) => {
            const novel = novelsMap.get(item.novelId);
            if (!novel) return null;
            return {
              novel,
              chapterNumber: item.chapterNumber,
              updatedAt: item.updatedAt,
            };
          })
          .filter(Boolean);
      }
    }

    return data;
  }, [sessionId, userId]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await fetchHistory();
      setHistory(data);
    } catch (error) {
      console.error('Failed to load reading history:', error);
      setError('Unable to load your reading history right now.');
    } finally {
      setLoading(false);
    }
  }, [fetchHistory]);

  useEffect(() => {
    // Wait until we know whether the user is logged in or anonymous.
    // The auth context may initialize asynchronously (userId starts as
    // null and becomes available after token validation).
    if (userId === null && !sessionId) return;

    let isMounted = true;

    const loadHistorySafely = async () => {
      try {
        const data = await fetchHistory();

        if (isMounted) {
          setHistory(data);
        }
      } catch (error) {
        console.error('Failed to load reading history:', error);
        if (isMounted) {
          setError('Unable to load your reading history right now.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadHistorySafely();

    return () => {
      isMounted = false;
    };
  }, [fetchHistory, userId, sessionId]);

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <section className="cr-section">
        <div className="container">
          <div className="disc-section__head">
            <h2>Continue <span className="accent">Reading</span></h2>
          </div>
          <div className="home-page__state home-page__state--error">
            <p>{error}</p>
            <Button variant="primary" onClick={loadHistory}>
              Retry
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (history.length === 0) {
    return null;
  }

  const renderCard = (item) => {
    const totalChapters = item.novel.totalChapters || 0;
    const hasStartedReading = item.chapterNumber !== null && item.chapterNumber !== undefined;

    const readChapters = Array.isArray(item.readChapters) ? item.readChapters : [];
    const maxReadChapter = readChapters.length > 0 ? Math.max(...readChapters) : null;

    const continueChapterNumber = hasStartedReading ? item.chapterNumber : null;
    const targetChapterNumber = Math.max(maxReadChapter ?? -1, continueChapterNumber ?? -1) >= 0
      ? Math.max(maxReadChapter ?? 0, continueChapterNumber ?? 0)
      : (totalChapters > 0 ? 0 : 0);

    const readCount = maxReadChapter !== null ? (maxReadChapter + 1) : 0;
    const newChaptersCount = Math.max(0, totalChapters - readCount);
    const hasNewChapters = newChaptersCount > 0;
    const progressPercent = totalChapters > 0 ? Math.min(100, Math.round((readCount / totalChapters) * 100)) : 0;
    const novelLink = hasNewChapters
      ? `/novels/${item.novel.id}/chapters/${targetChapterNumber}`
      : `/novels/${item.novel.id}`;

    return (
      <Link
        key={item.novel.id}
        to={novelLink}
        className="cr-card"
        data-testid={`novel-card-${item.novel.id}`}
        data-to={novelLink}
      >
        <div className="cr-card__cover">
          <img
            src={getNovelImageUrl(item.novel)}
            alt={item.novel.title}
            width="80"
            height="120"
            loading="lazy"
          />
        </div>
        <div className="cr-card__body">
          <div className="cr-card__title">{item.novel.title}</div>
          <div className="cr-card__chap">
            {hasStartedReading ? `Chapter ${targetChapterNumber}` : 'Start Reading'}
          </div>
          {hasStartedReading && totalChapters > 0 && (
            <div className="cr-card__bar">
              <span style={{ width: `${progressPercent}%` }} />
            </div>
          )}
          <div className="cr-card__meta">
            <span>{progressPercent}%</span>
            <span className="cr-card__resume">
              {hasStartedReading ? 'Continue' : 'Begin'}
            </span>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <HorizontalScrollList
      title="Continue"
      accentTitle="Reading"
      actionLabel="History →"
      onAction={() => navigate('/profile?tab=history')}
    >
      {history.map(renderCard)}
    </HorizontalScrollList>
  );
};

export default ContinueReading;
